import { generateWallet } from "@/moby/repo";
import { mui } from "@/mui";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { useState, useCallback, useEffect, useRef, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Redirect, useLocation } from "wouter";
import { z } from "zod";
import { OnboardingPageLayout } from "./onboarding-page-layout";
import { OnboardingPermissions, type EulaSignatureData } from "./onboarding-permissions";
import { useMobyRepo, useSession } from "./provider";
import Dexie from "dexie";
import { useLocationHash } from "@/hooks/useLocationHash";
import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { tryPersistWithoutPromptingUser } from "@/lib/storage";
import { MobyDB } from "@/moby/db";
import { XIcon, CheckCircle2Icon, XCircleIcon, ChevronDownIcon, InfoIcon } from "lucide-react";
import { MobyNetLogo } from "./moby-net-logo";
import { validateInviteCode, hashInviteCode, storeValidatedInvite, hasValidatedInvite } from "@/lib/invite-codes";
import { ed25519 } from "@noble/curves/ed25519";
import { BetaSignupDialog } from "./beta-signup-dialog";
import { EULA_VERSION } from "@/lib/eula-content";

// Base64 encoding compatible with tweetnacl-util's decodeBase64
function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Auto-detect email provider IMAP/SMTP settings from domain
const EMAIL_PROVIDER_SETTINGS: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }> = {
  "gmail.com": { imapHost: "imap.gmail.com", imapPort: 993, smtpHost: "smtp.gmail.com", smtpPort: 587 },
  "googlemail.com": { imapHost: "imap.gmail.com", imapPort: 993, smtpHost: "smtp.gmail.com", smtpPort: 587 },
  "yahoo.com": { imapHost: "imap.mail.yahoo.com", imapPort: 993, smtpHost: "smtp.mail.yahoo.com", smtpPort: 465 },
  "outlook.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "hotmail.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "live.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "icloud.com": { imapHost: "imap.mail.me.com", imapPort: 993, smtpHost: "smtp.mail.me.com", smtpPort: 587 },
  "me.com": { imapHost: "imap.mail.me.com", imapPort: 993, smtpHost: "smtp.mail.me.com", smtpPort: 587 },
  "aol.com": { imapHost: "imap.aol.com", imapPort: 993, smtpHost: "smtp.aol.com", smtpPort: 465 },
  "mobynet.io": { imapHost: "imap.gmail.com", imapPort: 993, smtpHost: "smtp.gmail.com", smtpPort: 587 },
};

function getProviderSettings(email: string) {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? EMAIL_PROVIDER_SETTINGS[domain] : undefined;
}

const DEFAULT_COLORS = ["#4CAF50", "#2196F3", "#9C27B0", "#FF9800", "#E91E63"];

export function OnboardingPage() {
  const session = useSession();
  const repo = useMobyRepo();
  const hash = useLocationHash();
  const wallets = useSuspenseLiveQuery(() => repo.getWallets(), []);
  const [, navigate] = useLocation();

  // ── Beta waitlist dialog (QR code entry) ─────────────────────────────────
  // Detects ?beta=1 (typically from a QR scan) and opens the waitlist dialog
  // over the normal onboarding page. Fully additive — closing the dialog
  // drops the user into the standard invite-gated flow. `ev=<eventId>` and
  // `ref=<waitlistId>` are optional attribution params.
  const [betaDialogOpen, setBetaDialogOpen] = useState(false);
  const [betaInitialEventId, setBetaInitialEventId] = useState<string | null>(null);
  const [betaInitialReferrer, setBetaInitialReferrer] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("beta") !== "1") return;

    setBetaInitialEventId(params.get("ev"));
    setBetaInitialReferrer(params.get("ref"));
    setBetaDialogOpen(true);

    // Scrub params so refresh/back doesn't retrigger, preserve hash for any
    // invite flow running underneath. Preserve other params (e.g. oauth_*).
    ["beta", "ev", "ref"].forEach((k) => params.delete(k));
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + (window.location.hash || "");
    window.history.replaceState({}, "", newUrl);
  }, []);

  // SAFE REDIRECT: useEffect fires AFTER paint, not during React's commit phase.
  // The old <Redirect> used useLayoutEffect (synchronous, during commit), which
  // mutated the URL via history.replaceState while React was committing.  That
  // triggered useSyncExternalStore tearing detection → error #185.
  // useEffect avoids this entirely — it runs after the browser has painted,
  // so all stores are consistent when the navigation fires.
  useEffect(() => {
    if (session === undefined) return;

    // OAUTH REDIRECT FIX: Intercept OAuth params for logged-in users before
    // redirect strips them.  When email-drawer initiates OAuth, Google
    // callback → /onboarding?oauth_code=...  Stash result for email-drawer.
    const params = new URLSearchParams(window.location.search);
    const oauthCode = params.get('oauth_code');
    const oauthError = params.get('oauth_error');
    const oauthState = params.get('oauth_state');

    if (oauthCode || oauthError) {
      if (oauthCode) localStorage.setItem('moby-oauth-code', oauthCode);
      if (oauthState) localStorage.setItem('moby-oauth-state-received', oauthState);
      if (oauthError) localStorage.setItem('moby-oauth-error', oauthError);
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
    }

    // If a beta QR was just scanned, don't bounce the logged-in user away
    // before they can complete (or dismiss) the beta waitlist dialog.
    if (betaDialogOpen) return;

    // setTimeout(0): escape React's effect flush to prevent useSyncExternalStore tearing.
    // wouter's navigate() calls history.replaceState synchronously → fires subscribers
    // mid-flush → error #185 if multiple effects navigate in the same flush.
    const t = setTimeout(() => navigate(`/chats${hash}`, { replace: true }), 0);
    return () => clearTimeout(t);
  }, [session, hash, navigate, betaDialogOpen]);

  // Beta dialog element — rendered on every code path so a QR scan works
  // even for logged-in users or returning peers. The dialog portals to
  // document.body, so it does not interfere with Redirect behavior.
  const betaDialogEl = (
    <BetaSignupDialog
      open={betaDialogOpen}
      onClose={() => setBetaDialogOpen(false)}
      initialEventId={betaInitialEventId}
      initialReferrer={betaInitialReferrer}
    />
  );

  // Hide onboarding UI while useEffect navigates to /chats
  if (session !== undefined) {
    return betaDialogEl;
  }

  // RETURNING PEER GUARD: If wallets already exist in the browser (peer logged
  // out but data persists), redirect to the login page instead of showing new
  // account creation. Allow through: OAuth callback returns (oauth_code/oauth_error
  // params) and explicit new-account requests (?new=1 from password recovery).
  if (wallets.length > 0 && !betaDialogOpen) {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('oauth_code') && !params.has('oauth_error') && !params.has('new')) {
      return <Redirect to={`/${hash}`} replace />;
    }
  }

  return (
    <>
      <OnboardingPageLayout
        title="Create Your Free Account"
        description="Sign up and create your free MobyChat account to send terabytes of confidential files, folders, and directories directly while you stream. Without the cloud."
        form={<OnboardingForm />}
      />
      {betaDialogEl}
    </>
  );
}

const onboardingFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required.")
      .max(24, "Name must be less than 24 characters.")
      .regex(
        /^[a-zA-Z0-9](?:[a-zA-Z0-9._]|(?:\s[a-zA-Z0-9]))*[a-zA-Z0-9]$/,
        "Name may only contain letters, numbers, spaces, and underscores.",
      ),
    password: z.string().min(1, "Password is required."),
    "confirm-password": z.string().min(1, "Confirm password is required."),
  })
  .refine(
    (v) => v.password === v["confirm-password"],
    "Passwords do not match.",
  );

export function OnboardingForm() {
  const db = useMobyRepo();
  const navigate = useLocation()[1];
  const hash = useLocationHash(); // Preserve invite hash through onboarding
  const formRef = useRef<HTMLFormElement>(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [permissionsCompleted, setPermissionsCompleted] = useState(false);
  const pendingFormData = useRef<{ name: string; password: string } | null>(null);
  const pendingEulaSignature = useRef<EulaSignatureData | null>(null);

  // Invite code state
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeValid, setInviteCodeValid] = useState<boolean | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // Email registration state (optional)
  const [emailAddress, setEmailAddress] = useState('');
  const [emailAppPassword, setEmailAppPassword] = useState('');
  const [emailTestStatus, setEmailTestStatus] = useState<'idle' | 'testing' | 'pass' | 'fail'>('idle');

  // OAuth state (Google, Outlook, etc.)
  const [oauthStatus, setOauthStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [oauthProvider, setOauthProvider] = useState<'google' | 'outlook' | 'yahoo' | null>(null);
  const oauthData = useRef<{
    email: string;
    name?: string;
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
    provider: 'google' | 'outlook' | 'yahoo';
  } | null>(null);

  // UI toggle for legacy (manual) form
  const [showLegacyForm, setShowLegacyForm] = useState(false);

  // Check if user already has a validated invite, or auto-populate from URL param
  useEffect(() => {
    if (hasValidatedInvite()) {
      setInviteCodeValid(true);
    }
    // Read invite code stashed by app.tsx from ?invite= URL param
    const pendingCode = localStorage.getItem('moby-pending-invite-code');
    if (pendingCode) {
      localStorage.removeItem('moby-pending-invite-code');
      const normalized = pendingCode.toUpperCase();
      setInviteCode(normalized);
      // Auto-validate
      if (normalized.length === 22) {
        handleInviteCodeChange(normalized);
      }
    }
  }, []);

  // Handle OAuth redirect return — detect oauth_code/oauth_state in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthCode = params.get('oauth_code');
    const oauthState = params.get('oauth_state');
    const oauthError = params.get('oauth_error');
    // Detect provider from callback (Outlook callback sets this; Google doesn't, defaults to 'google')
    const provider = (params.get('oauth_provider') || localStorage.getItem('moby-oauth-provider') || 'google') as 'google' | 'outlook' | 'yahoo';
    localStorage.removeItem('moby-oauth-provider');

    if (!oauthCode && !oauthError) return;

    // Clean URL immediately (remove query params, keep hash)
    const cleanUrl = window.location.pathname + (window.location.hash || '');
    window.history.replaceState({}, '', cleanUrl);

    if (oauthError) {
      toast.error(oauthError);
      setOauthStatus('error');
      return;
    }

    // Verify state matches what we saved before redirect
    const expectedState = localStorage.getItem('moby-oauth-state');
    localStorage.removeItem('moby-oauth-state');
    if (oauthState !== expectedState) {
      toast.error('OAuth state mismatch — please try again');
      setOauthStatus('error');
      return;
    }

    // Restore invite code validation (was saved before redirect)
    if (hasValidatedInvite()) {
      setInviteCodeValid(true);
    }

    // Check if this is a login-mode OAuth (returning user from login page)
    const isLoginMode = localStorage.getItem('moby-oauth-login-mode') === 'true';
    localStorage.removeItem('moby-oauth-login-mode');

    // Exchange code for tokens — route to the correct provider endpoint
    setOauthStatus('pending');
    setOauthProvider(provider);
    const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim() || window.location.origin;

    (async () => {
      try {
        const tokenRes = await fetch(`${gatewayUrl}/api/email/oauth/${provider}/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: oauthCode }),
        });
        const tokenData = await tokenRes.json();

        if (tokenData.success) {
          // Login-mode: returning user — look up stored wallet password and auto-login
          if (isLoginMode && tokenData.email) {
            const walletKey = `moby_${provider}_wallet_${tokenData.email.trim().toLowerCase()}`;
            const storedPassword = localStorage.getItem(walletKey);
            if (storedPassword) {
              try {
                const wallets = await db.getWallets();
                // Try deriving each wallet with the stored password to find the match
                for (const wallet of wallets) {
                  try {
                    const { deriveWallet: deriveWalletFn } = await import("@/moby/repo");
                    const derived = await deriveWalletFn(storedPassword, wallet.proof);
                    const derivedPubKey = bytesToHex(derived.publicKey);
                    if (derivedPubKey === wallet.address) {
                      // Match found — set session and navigate
                      // Update OAuth tokens for the email account (IndexedDB + localStorage)
                      try {
                        const newExpiry = Date.now() + (tokenData.expiresIn * 1000);
                        // Primary: write to wallet-scoped IndexedDB
                        const walletDb = new MobyDB(derivedPubKey);
                        try {
                          const entities = await walletDb.emailAccounts.toArray();
                          const match = entities.find(e => e.email.toLowerCase() === tokenData.email.toLowerCase());
                          if (match) {
                            await walletDb.emailAccounts.update(match.id, {
                              accessToken: tokenData.accessToken,
                              refreshToken: tokenData.refreshToken,
                              tokenExpiry: newExpiry,
                              updatedAt: Date.now(),
                            });
                            console.log('[ONBOARDING] OAuth tokens persisted to IndexedDB for', tokenData.email);
                          }
                        } finally {
                          walletDb.close();
                        }
                        // Backward-compat: also update localStorage
                        const stored = localStorage.getItem('moby_pref_emailAccounts');
                        const accounts = stored ? JSON.parse(stored) : [];
                        const emailAccount = accounts.find((a: Record<string, unknown>) =>
                          (a.email as string)?.toLowerCase() === tokenData.email.toLowerCase()
                        );
                        if (emailAccount) {
                          emailAccount.accessToken = tokenData.accessToken;
                          emailAccount.refreshToken = tokenData.refreshToken;
                          emailAccount.tokenExpiry = newExpiry;
                          localStorage.setItem('moby_pref_emailAccounts', JSON.stringify(accounts));
                        }
                      } catch { /* email token refresh is best-effort */ }

                      await db.setSession({
                        name: wallet.name,
                        address: derivedPubKey,
                        secret: bytesToHex(derived.secretKey),
                      });
                      toast.success(`Welcome back, ${wallet.name}!`);
                      navigate(`/chats${hash}`);
                      return;
                    }
                  } catch { /* password didn't match this wallet, try next */ }
                }
                toast.error('No matching account found. Please register a new account.');
              } catch (err) {
                toast.error('Failed to look up wallet.');
                console.error('[ONBOARDING] Login-mode OAuth wallet lookup failed:', err);
              }
            } else {
              toast.error('No saved credentials for this account. Please register a new account.');
            }
            setOauthStatus('error');
            return;
          }

          oauthData.current = {
            email: tokenData.email,
            name: tokenData.name,
            accessToken: tokenData.accessToken,
            refreshToken: tokenData.refreshToken,
            expiresIn: tokenData.expiresIn,
            provider,
          };
          setEmailAddress(tokenData.email);
          setEmailTestStatus('pass');
          setOauthStatus('success');
          toast.success(`Signed in as ${tokenData.email}`);
          // Auto-proceed to permissions/EULA (invite was validated before redirect)
          proceedWithOAuth();
        } else {
          toast.error(tokenData.error || 'Sign-In failed');
          setOauthStatus('error');
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Token exchange failed');
        setOauthStatus('error');
      }
    })();
  }, []);

  // Validate invite code when it reaches 22 characters
  const handleInviteCodeChange = useCallback(async (code: string) => {
    const normalized = code.toUpperCase();
    setInviteCode(normalized);

    if (normalized.length === 22) {
      setIsValidatingCode(true);
      try {
        const isValid = await validateInviteCode(normalized);
        setInviteCodeValid(isValid);
        if (isValid) {
          const codeHash = await hashInviteCode(normalized);
          storeValidatedInvite(codeHash);
          toast.success("Invite code validated! Welcome to MobyChat Beta.");
          // If OAuth already completed, auto-proceed
          if (oauthData.current && oauthStatus === 'success') {
            proceedWithOAuth();
          }
        } else {
          toast.error("Invalid invite code. Please check and try again.");
        }
      } catch {
        setInviteCodeValid(false);
        toast.error("Error validating invite code.");
      } finally {
        setIsValidatingCode(false);
      }
    } else if (normalized.length > 0) {
      setInviteCodeValid(null);
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);

    const form = onboardingFormSchema.safeParse({
      name: data.get("name"),
      password: data.get("password"),
      "confirm-password": data.get("confirm-password"),
    });


    if (!form.success) {
      for (const error of form.error.errors) {
        toast.error(error.message);
      }
      return;
    }

    // Validate invite code before proceeding
    if (inviteCodeValid !== true) {
      toast.error("Please enter a valid invite code.");
      return;
    }

    // If email fields are filled (and not OAuth), test IMAP before proceeding
    const hasEmailCredentials = emailAddress.trim() && emailAppPassword.trim();
    const isOAuthEmail = oauthData.current !== null && oauthStatus === 'success';
    if (hasEmailCredentials && !isOAuthEmail && emailTestStatus !== 'pass') {
      // If not yet tested or previously failed, run IMAP test
      if (emailTestStatus !== 'testing') {
        const passed = await testImapConnection();
        if (!passed) return; // Block until credentials pass
      } else {
        return; // Already testing, wait
      }
    }

    // Store form data and show permissions drawer if not completed
    if (!permissionsCompleted) {
      pendingFormData.current = { name: form.data.name, password: form.data.password };
      setShowPermissions(true);
      return;
    }

    // If permissions already completed, create account directly
    await createAccount(form.data.name, form.data.password);
  };

  // Test IMAP connection via gateway
  const testImapConnection = async (): Promise<boolean> => {
    // Use same key as email-drawer, fallback to current origin
    let gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim() || '';
    if (!gatewayUrl) {
      gatewayUrl = window.location.origin;
      localStorage.setItem('moby_pref_emailGatewayUrl', gatewayUrl);
    }

    const email = emailAddress.trim();
    const settings = getProviderSettings(email);
    if (!settings) {
      toast.error("Unsupported email provider. Please use Gmail, Outlook, Yahoo, iCloud, or AOL.");
      setEmailTestStatus('fail');
      return false;
    }

    setEmailTestStatus('testing');

    try {
      const response = await fetch(`${gatewayUrl}/api/email/imap/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: settings.imapHost,
          port: settings.imapPort,
          secure: true,
          credentials: { user: email, pass: emailAppPassword },
        }),
      });

      const data = await response.json().catch(() => ({ success: false, error: "Invalid response" }));

      if (response.ok && data.success) {
        setEmailTestStatus('pass');
        return true;
      } else {
        setEmailTestStatus('fail');
        return false;
      }
    } catch {
      setEmailTestStatus('fail');
      return false;
    }
  };

  // Generic OAuth flow — works for any provider with config/auth-url/callback/token pattern
  const startOAuth = useCallback(async (provider: 'google' | 'outlook' | 'yahoo') => {
    let gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim() || '';
    if (!gatewayUrl) {
      gatewayUrl = window.location.origin;
      localStorage.setItem('moby_pref_emailGatewayUrl', gatewayUrl);
    }

    const providerLabel = provider === 'google' ? 'Google' : provider === 'outlook' ? 'Outlook' : 'Yahoo';
    setOauthStatus('pending');
    setOauthProvider(provider);

    try {
      // Check if OAuth is configured
      const configRes = await fetch(`${gatewayUrl}/api/email/oauth/${provider}/config`);
      const configData = await configRes.json();
      if (!configData.configured) {
        toast.error(`${providerLabel} Sign-In is not configured on this server yet.`);
        setOauthStatus('error');
        return;
      }

      // Get auth URL
      const authRes = await fetch(`${gatewayUrl}/api/email/oauth/${provider}/auth-url`);
      const authData = await authRes.json();
      if (!authData.success || !authData.authUrl) {
        toast.error(authData.error || `Failed to start ${providerLabel} Sign-In`);
        setOauthStatus('error');
        return;
      }

      // Save state + provider for verification on return
      localStorage.setItem('moby-oauth-state', authData.state);
      localStorage.setItem('moby-oauth-provider', provider);

      // Redirect to provider (same tab) — callback redirects back to /onboarding
      window.location.href = authData.authUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `${providerLabel} Sign-In failed`);
      setOauthStatus('error');
    }
  }, []);

  const startGoogleOAuth = useCallback(() => startOAuth('google'), [startOAuth]);
  const startOutlookOAuth = useCallback(() => startOAuth('outlook'), [startOAuth]);
  const startYahooOAuth = useCallback(() => startOAuth('yahoo'), [startOAuth]);

  // OAuth auto-proceed: generate random password, use provider display name, trigger permissions
  const proceedWithOAuth = useCallback(() => {
    if (!oauthData.current) return;
    const displayName = oauthData.current.name
      ? oauthData.current.name.replace(/[^a-zA-Z0-9 _.]/g, '').slice(0, 24).trim()
      : oauthData.current.email.split('@')[0].slice(0, 24);
    // Generate a random password the user never sees (wallet encryption only)
    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);
    const randomPassword = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
    pendingFormData.current = { name: displayName, password: randomPassword };
    setShowPermissions(true);
  }, []);

  // Save email account locally + register with MobyChat MongoDB
  const registerAndSaveEmail = async (walletAddress: string, walletSecret: string) => {
    const email = emailAddress.trim();
    const isOAuth = oauthData.current !== null && oauthStatus === 'success';
    if (!email || (!isOAuth && emailTestStatus !== 'pass')) return;

    const oauthProviderSettings: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }> = {
      google: { imapHost: 'imap.gmail.com', imapPort: 993, smtpHost: 'smtp.gmail.com', smtpPort: 587 },
      outlook: { imapHost: 'outlook.office365.com', imapPort: 993, smtpHost: 'smtp.office365.com', smtpPort: 587 },
      yahoo: { imapHost: 'imap.mail.yahoo.com', imapPort: 993, smtpHost: 'smtp.mail.yahoo.com', smtpPort: 465 },
    };
    const settings = isOAuth && oauthData.current
      ? oauthProviderSettings[oauthData.current.provider] || oauthProviderSettings.google
      : getProviderSettings(email);
    if (!settings) return;

    // Save to localStorage — MobyDB for this wallet doesn't exist in React context yet
    // (wallet was just created, provider hasn't re-rendered). The email-account-store
    // migration layer will move this to IndexedDB on first getEmailAccountsFromDB() call
    // when the user lands on /chats (milliseconds after onboarding completes).
    try {
      const stored = localStorage.getItem('moby_pref_emailAccounts');
      const accounts = stored ? JSON.parse(stored) : [];
      const newAccount: Record<string, unknown> = {
        id: crypto.randomUUID(),
        email,
        username: email,
        password: isOAuth ? '' : emailAppPassword,
        imapHost: settings.imapHost,
        imapPort: settings.imapPort,
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        color: DEFAULT_COLORS[accounts.length % DEFAULT_COLORS.length],
        enabled: true,
        authType: isOAuth ? 'oauth2' : 'password',
        registeredWithMobyChat: true,
        isPrimary: false,
      };
      if (isOAuth && oauthData.current) {
        newAccount.accessToken = oauthData.current.accessToken;
        newAccount.refreshToken = oauthData.current.refreshToken;
        newAccount.tokenExpiry = Date.now() + (oauthData.current.expiresIn * 1000);
      }
      accounts.push(newAccount);
      localStorage.setItem('moby_pref_emailAccounts', JSON.stringify(accounts));
      window.dispatchEvent(new CustomEvent('user-preferences-updated'));
    } catch (err) {
      console.error('[ONBOARDING] Failed to save email account locally:', err);
    }

    // Register with MobyChat MongoDB
    let gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim() || '';
    if (!gatewayUrl) {
      gatewayUrl = window.location.origin;
      localStorage.setItem('moby_pref_emailGatewayUrl', gatewayUrl);
    }

    try {
      const timestamp = Date.now().toString();
      const requestBody = JSON.stringify({ email, isPrimary: false });
      const messageToSign = `${timestamp}${requestBody}`;
      const messageBytes = new TextEncoder().encode(messageToSign);
      const signature = ed25519.sign(messageBytes, hexToBytes(walletSecret));
      const signatureBase64 = encodeBase64(signature);
      const addressBase64 = encodeBase64(hexToBytes(walletAddress));

      await fetch(`${gatewayUrl}/api/user/register-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Moby-Address": addressBase64,
          "X-Moby-Timestamp": timestamp,
          "X-Moby-Signature": signatureBase64,
        },
        body: requestBody,
      });
    } catch (err) {
      console.warn('[ONBOARDING] Email registration with MobyChat failed:', err);
    }
  };

  const createAccount = async (name: string, password: string) => {
    // Storage check — softened to warning. Persistent storage is optional;
    // the silent retry in MobyProvider will keep trying on future visits.
    const storage = await tryPersistWithoutPromptingUser();
    if (storage === "never") {
      toast.warning(
        "This browser may not fully support persistent storage. Your data could be lost if the browser clears storage. Consider using Google Chrome for the best experience.",
        { duration: 8000 }
      );
    }

    const wallet = await generateWallet(password);
    const walletAddress = bytesToHex(wallet.publicKey);
    const walletSecret = bytesToHex(wallet.secretKey);

    try {
      await db.transaction("rw", db.wallets, db.kvs, async () => {
        await db.storeWallet({
          name: name,
          address: walletAddress,
          proof: bytesToHex(wallet.proof),
        });

        await db.setSession({
          name: name,
          address: walletAddress,
          secret: walletSecret,
        });
      });
    } catch (err) {
      if (err instanceof Dexie.ConstraintError) {
        toast.error(
          "You already have another account with this name. Please try again.",
        );
        return;
      }
      throw err;
    }

    // For OAuth accounts, store wallet password keyed by provider+email so returning
    // users can re-login via OAuth on the login page without knowing the random password
    if (oauthData.current && oauthStatus === 'success' && emailAddress.trim()) {
      const providerKey = oauthData.current.provider || 'google';
      localStorage.setItem(`moby_${providerKey}_wallet_${emailAddress.trim().toLowerCase()}`, password);
    }

    // Send EULA acceptance to MongoDB (truly non-blocking, fire-and-forget)
    if (pendingEulaSignature.current) {
      const sig = pendingEulaSignature.current;
      pendingEulaSignature.current = null;
      
      // Fire-and-forget - don't await to prevent blocking navigation.
      // Audit payload uses ISO 3166 codes now, with legacy display-name
      // fields retained so existing backend consumers don't break.
      const acceptedAt = new Date().toISOString();
      fetch('/api/eula/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          peerAddress: walletAddress,
          fullName: sig.fullName,
          countryCode: sig.countryCode,
          countryName: sig.countryName,
          subdivisionCode: sig.subdivisionCode,
          subdivisionName: sig.subdivisionName,
          subdivisionInputType: sig.subdivisionInputType,
          // Legacy field names for backward compat with existing server.
          country: sig.country,
          stateRegion: sig.stateRegion,
          email: emailAddress.trim() || undefined,
          eulaVersion: sig.eulaVersion || EULA_VERSION,
          appVersion: localStorage.getItem('moby_app_version') || '1.0.0',
          acceptedAt,
        }),
      }).catch(err => {
        console.warn('[ONBOARDING] Failed to send EULA acceptance (non-critical):', err);
      });

      console.log('[ONBOARDING] EULA acceptance initiated (version:', sig.eulaVersion || EULA_VERSION, ')');

      // Also persist locally for offline reference
      localStorage.setItem('moby_eula_accepted', JSON.stringify({
        fullName: sig.fullName,
        countryCode: sig.countryCode,
        countryName: sig.countryName,
        subdivisionCode: sig.subdivisionCode,
        subdivisionName: sig.subdivisionName,
        subdivisionInputType: sig.subdivisionInputType,
        // Legacy fields kept for backward compat with any local readers.
        country: sig.country,
        stateRegion: sig.stateRegion,
        eulaVersion: sig.eulaVersion || EULA_VERSION,
        acceptedAt,
      }));
    }

    // Navigate to chats with hash preserved for invite flow.
    // This is the PRIMARY navigation — it changes the URL synchronously so that
    // when React flushes re-renders, the Switch matches /chats and ChatsPage
    // renders (OnboardingPage unmounts, its useEffect redirect never fires).
    // The useEffect redirect in OnboardingPage is a safety net for edge cases
    // (e.g. logged-in user visiting /onboarding directly).
    navigate(`/chats${hash}`);

    // Register email with MobyChat if credentials were provided and validated.
    // The synchronous localStorage writes inside this function complete before
    // its first internal `await fetch(...)`, so email data is available when
    // ChatsPage mounts.
    await registerAndSaveEmail(walletAddress, walletSecret);
  };

  const handlePermissionsComplete = async (eulaSignature?: EulaSignatureData) => {
    setPermissionsCompleted(true);
    setShowPermissions(false);

    // Store EULA signature data for sending to MongoDB after wallet creation
    if (eulaSignature) {
      pendingEulaSignature.current = eulaSignature;
    }

    // Auto-proceed with account creation using stored form data
    if (pendingFormData.current) {
      const { name, password } = pendingFormData.current;
      pendingFormData.current = null;
      await createAccount(name, password);
    }
  };

  return (
    <>
      {/* Permissions Drawer — portaled to body so it escapes layout overflow */}
      {showPermissions && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface rounded-lg shadow-xl">
            <button
              onClick={() => setShowPermissions(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-surface-variant"
              aria-label="Close permissions dialog"
              title="Close"
            >
              <XIcon size={20} />
            </button>
            <OnboardingPermissions 
              onComplete={handlePermissionsComplete}
              skipable={false}
              isDrawer={true}
            />
          </div>
        </div>,
        document.body
      )}

      <form ref={formRef} onSubmit={onSubmit}>
        <div className="border border-outline-variant rounded-xl bg-surface/80 backdrop-blur-sm p-4 flex flex-col gap-2">
          <div className="flex items-center gap-3 mb-1">
            <MobyNetLogo className="h-12" />
            <h1 className="text-body-lg text-on-surface font-semibold tracking-wider">MobyNet™</h1>
          </div>
          <input hidden type="text" autoComplete="username" />

          {/* Invite Code */}
          <div>
            <label className="text-label-sm text-on-surface-variant block mb-1">Invite Code</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => handleInviteCodeChange(e.target.value)}
                placeholder="XXXX-XXXX-XXXX-XXXX-XX"
                maxLength={22}
                disabled={inviteCodeValid === true}
                className={`w-full px-3 py-2 rounded border bg-surface text-on-surface text-body-sm ${
                  inviteCodeValid === true
                    ? 'border-success bg-success/10'
                    : inviteCodeValid === false
                    ? 'border-error bg-error/10'
                    : 'border-outline-variant'
                } font-mono tracking-wider`}
              />
              {inviteCodeValid === true && (
                <CheckCircle2Icon className="text-success shrink-0" size={18} />
              )}
              {inviteCodeValid === false && inviteCode.length === 22 && (
                <XCircleIcon className="text-error shrink-0" size={18} />
              )}
              {isValidatingCode && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary shrink-0" />
              )}
            </div>
            {inviteCodeValid === false && inviteCode.length === 22 && (
              <p className="text-body-sm text-error mt-0.5">Invalid code</p>
            )}
          </div>

          {/* OAuth sign-in buttons */}
          <div className="flex flex-col gap-2">
            {/* Sign in with Google */}
            <button
              type="button"
              className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
                inviteCodeValid === true && oauthStatus !== 'success' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={startGoogleOAuth}
              disabled={inviteCodeValid !== true || oauthStatus === 'pending' || oauthStatus === 'success'}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-body-md text-on-surface font-medium">
                {oauthStatus === 'pending' && oauthProvider === 'google' ? 'Signing in...' : oauthStatus === 'success' && oauthProvider === 'google' ? `Signed in as ${emailAddress}` : 'Sign in with Google'}
              </span>
            </button>

            {/* Sign in with Outlook */}
            <button
              type="button"
              className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
                inviteCodeValid === true && oauthStatus !== 'success' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={startOutlookOAuth}
              disabled={inviteCodeValid !== true || oauthStatus === 'pending' || oauthStatus === 'success'}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.588.234h-8.522v-8.07l1.5 1.11 1.848-1.31V8.39l-1.848 1.31-1.5-1.14v-.173H24z" fill="#0364B8"/>
                <path d="M16.5 10.405l-1.848 1.31-1.5-1.11v8.07h-4.5V7.387h7.848v2.018z" fill="#0078D4"/>
                <path d="M8.652 4.652v15.348A.782.782 0 0 1 7.87 20.8L0 18.652V5.348L7.87 3.2a.782.782 0 0 1 .782.782v.67z" fill="#0078D4"/>
                <path d="M5.87 9.174c-.392-.234-.87-.352-1.435-.352-.609 0-1.1.2-1.478.6-.378.4-.566.918-.566 1.556 0 .66.183 1.192.548 1.596.366.404.848.606 1.448.606.574 0 1.06-.122 1.457-.366v1.174c-.43.2-.944.3-1.54.3-.896 0-1.613-.287-2.152-.86C1.384 12.854 1.152 12.109 1.152 11c0-1.122.283-2.009.848-2.661.566-.652 1.322-.978 2.27-.978.548 0 1.04.1 1.474.296v1.517h.126z" fill="#fff"/>
              </svg>
              <span className="text-body-md text-on-surface font-medium">
                {oauthStatus === 'pending' && oauthProvider === 'outlook' ? 'Signing in...' : oauthStatus === 'success' && oauthProvider === 'outlook' ? `Signed in as ${emailAddress}` : 'Sign in with Outlook'}
              </span>
            </button>

            {/* Sign in with Yahoo */}
            <button
              type="button"
              className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
                inviteCodeValid === true && oauthStatus !== 'success' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={startYahooOAuth}
              disabled={inviteCodeValid !== true || oauthStatus === 'pending' || oauthStatus === 'success'}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M13.371 4l-1.737 4.028L9.898 4H5l4.474 8.884L7.654 20h4.898l1.812-7.116L18.838 20h4.898l-1.82-7.116L24 4h-4.898l-1.737 4.028L15.628 4h-2.257z" fill="#720E9E"/>
                <path d="M0 7.5h4.5v12H0z" fill="#720E9E"/>
                <circle cx="2.25" cy="4.25" r="2.25" fill="#720E9E"/>
              </svg>
              <span className="text-body-md text-on-surface font-medium">
                {oauthStatus === 'pending' && oauthProvider === 'yahoo' ? 'Signing in...' : oauthStatus === 'success' && oauthProvider === 'yahoo' ? `Signed in as ${emailAddress}` : 'Sign in with Yahoo'}
              </span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-outline-variant" />
            <span className="text-label-sm text-on-surface-variant">or</span>
            <div className="flex-1 border-t border-outline-variant" />
          </div>

          {/* Collapsible manual setup section */}
          <button
            type="button"
            onClick={() => setShowLegacyForm(!showLegacyForm)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-outline-variant bg-surface hover:bg-surface-variant transition-colors"
          >
            <span className="text-body-sm text-on-surface-variant">Create account manually</span>
            <ChevronDownIcon
              size={18}
              className={`text-on-surface-variant transition-transform duration-200 ${showLegacyForm ? 'rotate-180' : ''}`}
            />
          </button>

          {showLegacyForm && (
            <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
              <input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                minLength={1}
                maxLength={24}
                placeholder="User Name"
                className="w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface text-body-md font-medium"
              />
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
                minLength={1}
                placeholder="Password"
                className="w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface text-body-md font-medium"
              />
              <input
                type="password"
                id="confirm-password"
                name="confirm-password"
                autoComplete="new-password"
                minLength={1}
                placeholder="Confirm Password"
                className="w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface text-body-md font-medium"
              />

              {/* Email (Optional) */}
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    readOnly={oauthStatus === 'success'}
                    onChange={(e) => {
                      setEmailAddress(e.target.value);
                      if (emailTestStatus !== 'idle') setEmailTestStatus('idle');
                      if (oauthData.current) {
                        oauthData.current = null;
                        setOauthStatus('idle');
                      }
                    }}
                    placeholder="Email Address (optional)"
                    className={`w-full px-3 py-2 rounded border bg-surface text-on-surface text-body-sm ${
                      emailTestStatus === 'pass'
                        ? 'border-success bg-success/10'
                        : emailTestStatus === 'fail'
                        ? 'border-error bg-error/10'
                        : 'border-outline-variant'
                    } ${oauthStatus === 'success' ? 'opacity-75 cursor-not-allowed' : ''}`}
                  />
                  {emailTestStatus === 'pass' && (
                    <CheckCircle2Icon className="text-success shrink-0" size={18} />
                  )}
                  {emailTestStatus === 'fail' && (
                    <XCircleIcon className="text-error shrink-0" size={18} />
                  )}
                  {emailTestStatus === 'testing' && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary shrink-0" />
                  )}
                </div>
                {oauthStatus === 'success' && (
                  <p className="text-body-sm text-success mt-0.5">Google account linked</p>
                )}
                {oauthStatus !== 'success' && (
                  <>
                    <input
                      type="password"
                      value={emailAppPassword}
                      onChange={(e) => {
                        setEmailAppPassword(e.target.value);
                        if (emailTestStatus !== 'idle') setEmailTestStatus('idle');
                      }}
                      placeholder="App Password (optional)"
                      className={`w-full mt-2 px-3 py-2 rounded border bg-surface text-on-surface text-body-sm ${
                        emailTestStatus === 'fail'
                          ? 'border-error bg-error/10'
                          : 'border-outline-variant'
                      }`}
                    />
                    <div className="flex items-start gap-1.5 mt-1.5 px-1">
                      <InfoIcon size={13} className="text-on-surface-variant shrink-0 mt-0.5" />
                      <p className="text-label-sm text-on-surface-variant leading-tight">
                        This is <strong>not</strong> your email account password. You need to generate an{' '}
                        <a
                          href="https://support.google.com/accounts/answer/185833"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >App Password</a>{' '}
                        in your email provider's security settings. Google: Security → 2-Step Verification → App Passwords.
                      </p>
                    </div>
                  </>
                )}
                {emailTestStatus === 'fail' && oauthStatus !== 'success' && (
                  <p className="text-body-sm text-error mt-0.5">Authentication failed</p>
                )}
              </div>

              {permissionsCompleted && (
                <div className="flex items-center gap-2 text-success text-body-sm">
                  <CheckCircle2Icon size={16} />
                  <span>Permissions configured</span>
                </div>
              )}

              <div className="flex justify-end">
                <button type="submit" className="px-5 py-2 rounded-lg bg-primary text-on-primary text-body-md font-medium hover:opacity-90 transition-opacity">
                  Create account
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
}
